import React, { useEffect, useState } from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import FullScreenModal from 'Components/Atoms/FullScreenModal';
import Button from 'Components/Atoms/Button';
import { ReservationShift, ReservationLength } from 'types/shifts';
import { FloorPlan } from 'types/reservations';
import SlotSettings, { SlotState } from 'App/Settings/Components/SlotSettings';
import ReservationLengthRules from '../ReservationLengthRules';
import { ActiveShift } from 'CustomHooks/useCalendarDates';
import { applyDayOverrideChanges, changeShiftSettings, effectiveShiftSettings } from 'utils/dayOverrides';
import DayCapacityDetails from 'Components/Organisms/DayCapacityDetails';

export type DetailedShiftSettingsProps = {
  activeShift: NonNullable<ActiveShift<ReservationShift>>;
  setactiveShift: React.Dispatch<React.SetStateAction<ActiveShift<ReservationShift>>>;
  settings: {
    occassions: { id: string; title: string; hidden?: boolean }[];
    spaces: { id: string; title: string }[];
    floorPlans: FloorPlan[];
  };
};

const DEFAULT_SLOT_STATE = {
  slotsPerBlock: {
    rules: [],
    standard: 20,
  },
  blockedAt: [],
  start: 0,
  close: 96,
  service: 88,
};

const DetailedShiftSettings: React.FC<DetailedShiftSettingsProps> = ({
  activeShift,
  setactiveShift,
  settings,
}) => {
  const [open, setopen] = useState(false);
  const [slotstate, setslotstate] = useState<null | SlotState>(null);
  const [reservationLength, setreservationLength] =
    useState<null | ReservationLength>(null);

  const series = !!activeShift.allUpcomingShifts;
  const dayOnly = activeShift.type === 'edit' && !series;

  useEffect(() => {
    if (!activeShift.shift.closed) {
      const effective = effectiveShiftSettings(activeShift.shift, series);
      setslotstate({
        slotsPerBlock:
          effective.slotsPerBlock ??
          DEFAULT_SLOT_STATE.slotsPerBlock,
        blockedAt:
          effective.blockedAt ??
          DEFAULT_SLOT_STATE.blockedAt,
        start: activeShift.shift.start ?? DEFAULT_SLOT_STATE.start,
        close: activeShift.shift.close ?? DEFAULT_SLOT_STATE.close,
        service: activeShift.shift.service ?? DEFAULT_SLOT_STATE.service,
        maxGuests: effective.maxGuests,
        countAllGuests: effective.countAllGuests,
      });
      setreservationLength(
        effective.reservationLength ?? {
          rules: [],
          standard: 8,
        }
      );
    }
  }, [open, activeShift.shift, series]);

  const handleSubmit = () => {
    if (!slotstate) return;
    setactiveShift(current => current ? {
      ...current,
      shift: changeShiftSettings(current.shift, {
        slotsPerBlock: {
          ...slotstate.slotsPerBlock,
          rules: (slotstate.slotsPerBlock.rules ?? []).map(rule => ({ ...rule, slots: rule.slots ?? rule.number ?? 0 })),
        },
        blockedAt: slotstate.blockedAt,
        reservationLength: reservationLength ?? { rules: [], standard: 8 },
        maxGuests: slotstate.maxGuests,
        countAllGuests: slotstate.countAllGuests,
      }, dayOnly),
    } : current);
    setopen(false);
  };

  return (
    <>
      <DayCapacityDetails shift={activeShift.shift} fields={['maxGuests', 'slotsPerBlock']} series={series} onReset={dayOnly ? field => {
        setactiveShift(current => current ? { ...current, shift: applyDayOverrideChanges(current.shift, { set: {}, reset: [field] }) } : current);
      } : undefined} />
      <Button variant="secondary" onClick={() => setopen(true)}>
        Block Settings
      </Button>
      <FullScreenModal
        open={open}
        onClose={() => setopen(false)}
        title="Block Settings"
        onSubmit={handleSubmit}
      >
        {slotstate && (
          <SlotSettings state={slotstate} onChange={(st) => setslotstate(st)} />
        )}
        {reservationLength && (
          <ReservationLengthRules
            reservationLength={reservationLength}
            onChange={(st) => setreservationLength(st)}
            occassions={settings.occassions}
            currentShift={activeShift?.shift}
          />
        )}
      </FullScreenModal>
    </>
  );
};

export default DetailedShiftSettings;
